import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import Components, { textFieldValidationStates } from '@u4/next-components';
import '@u4/next-components/lib/style.css';
import {
   setUserId,setPassword,
   setWebsite, 
   setDocumentTypeOptions,
   setEmployersOptions,
   setDocumentType,
   setParameters,
   checkEmployers,setTranslations,
   sendAsync, isValid,
  setError, setErrorMessage, setSuccess, finishSending
} from './retrieveFormSlice';
import EmployerDto, { EmployerWithHighWaterMark } from '../../app/dto/employerDto';
import DocumentTypeDto from '../../app/dto/documentTypeDto';
import {sendTranslationRequest, sendApiRequest} from '../../app/sender';
import { useAccessToken } from '../../app/authorization';
import _ from 'lodash';
import './retrieveFormStyles.css';
import {RootState} from '../../app/store';
import { useApi } from '../../app/apiClient';
import OperationDto from '../../app/dto/operationDto';
import Modal from '../Modal/Modal';
import { useConfig } from '../../config/appconfig';
import ParameterDto from '../../app/dto/parameterDto';
import { getOperationErrorMessage } from '../../app/errors/operationErrorMessages';

export const RetrieveForm: React.FunctionComponent = () => {
  const { userId, password, website, documentType, documentTypesOptions, websiteOptions,
    employersOptions, checkedEmployers, parameters, translations, isSending, isError, errorMessage, isSuccess } = useSelector(
      (state: RootState) => {
        return {
          userId: state.retrieveForm.userId,
          password: state.retrieveForm.userPassword,
          website: state.retrieveForm.website,
          documentType: state.retrieveForm.documentType,
          documentTypesOptions: state.retrieveForm.documentTypeOptions,
          websiteOptions: state.retrieveForm.websiteOptions,
          employersOptions: state.retrieveForm.employersOptions,
          checkedEmployers: state.retrieveForm.employers,
          parameters: state.retrieveForm.parameters,
          translations: state.retrieveForm.translations,
          isSending: state.retrieveForm.isSending,
          isError: state.retrieveForm.isError,
          errorMessage: state.retrieveForm.errorMessage,
          isSuccess: state.retrieveForm.isSuccess,
        };
      }
    );

  const config = useConfig();
  const isDataValid = useSelector(isValid);
  const token = useAccessToken();
  const [validate, setValidate] = useState(false);
  const { operationId } = useParams();
  const dispatch = useDispatch();
  const apiClient = useApi();
  let interval: number;

  const [employersWithSequenceNo, setEmployersWithSequenceNo] = useState<EmployerWithHighWaterMark[]>([]);

  const employersTableColumns = [
    { title: translations.Employers, fieldTemplate: '{name}', editable: false, width: 'LARGE', sortable: false },
    { title: translations.SequenceNo, fieldTemplate: '{sequenceNo}', editable: true, width: 'SMALL', sortable: false }
  ];

  function fetchTranslations(): void {
    sendTranslationRequest('/bundles/hmrcRetrieve', config, token)
      .then(res => dispatch(setTranslations(res)));
  }

  function fetchEmployers(): void {
    sendApiRequest<Array<EmployerDto>>('employers?operationId=' + operationId, 'GET', config, token)
      .then(res => dispatch(setEmployersOptions(res)));
  }

  function fetchDocumentTypes(): void {
    sendApiRequest<Array<DocumentTypeDto>>('documentTypes?operationId=' + operationId, 'GET', config, token)
      .then(res => dispatch(setDocumentTypeOptions(res)));
  }

  function fetchParameters(): void {
    sendApiRequest<Array<ParameterDto>>('parameters/all', 'GET', config, token)
      .then(res => dispatch(setParameters(res)));
  }

  function fetchOperationStatus(): void {
    sendApiRequest<OperationDto>(`operations/${operationId}`, 'GET', config, token)
      .then(res => checkOperationStatus(res));
  }

  function checkOperationStatus(res: OperationDto): void {
    switch(res.status){
      case 'Done': {
        window.clearInterval(interval);
        dispatch(setSuccess(true));
        dispatch(finishSending());
        break;
      }
      case 'Error': {
        dispatch(setErrorMessage(getOperationErrorMessage(res.errorCode, res.errorMessage)));
        window.clearInterval(interval);
        dispatch(setError(true));
        dispatch(finishSending());
        break;
      }
    }
  }

  function startInterval(): void{
    interval = window.setInterval(() => {
      fetchOperationStatus();
    }, 5000);    
  }

  useEffect(() => {
    if (token) {
      fetchTranslations();
      fetchEmployers();
      fetchDocumentTypes();
      fetchParameters();
    }
  }, [token]);

  function shouldShowEmployersValidation(){
    return _.size(checkedEmployers) == 0 && validate;
  }

  async function handleSend(_e: React.MouseEvent): Promise<void> {
    setValidate(true);

    if (operationId) {
      await dispatch(sendAsync(
        apiClient,
        {
          OperationId: operationId,
          User: userId,
          Password: password,
          WebSite: website,
          Employers: checkedEmployers,
          DocumentType: documentType,
          Timeout: config.requestTimeout
        }));

        if(!isError){
          startInterval();
        }
   }
  }

  function getTableValidationCssClass(){
    let cssClass = 'validated-element';
    if(_.size(employersOptions) > 0)
      cssClass += '-margin';
    if(!shouldShowEmployersValidation())
      cssClass += '--hidden';  

    return cssClass;
  }

  useEffect(() => {
    if (documentType && website && employersOptions.length > 0) {
      const initialData = employersOptions.map(employer => {
        const parameter = parameters.find(param =>
          param.name === 'HighWaterMark' &&
          param.employer === employer.name &&
          param.documentType === documentType.replace('PAYE-', '') &&
          param.enviroment === website.replace(' site', '')
        );
        
        return {
          ...employer,
          sequenceNo: parameter?.value || '0'
        };
      });
      setEmployersWithSequenceNo(initialData);
    }
  }, [documentType, website]);

  function handleSelectionChanged(itemIndexes: Array<string>) {
    const selectedEmployers: EmployerWithHighWaterMark[] = itemIndexes.map(index => {
      const employer = employersWithSequenceNo[parseInt(index)];
      return {
        name: employer.name,
        sequenceNo: employer.sequenceNo
      };
    });

    dispatch(checkEmployers(selectedEmployers));
  }

  function handleEdit(event: any) {
    const { rowIndex, updated } = event.target;
    
    setEmployersWithSequenceNo(prevEmployers => {
      const updatedEmployers = [...prevEmployers];
      updatedEmployers[rowIndex] = {
        ...updatedEmployers[rowIndex],
        ...updated
      };
      return updatedEmployers;
    });

    const editedEmployerName = employersWithSequenceNo[rowIndex]?.name;
    if (editedEmployerName && updated.sequenceNo !== undefined) {
      const updatedCheckedEmployers = checkedEmployers.map(emp =>
        emp.name === editedEmployerName
          ? { ...emp, sequenceNo: updated.sequenceNo }
          : emp
      );
      dispatch(checkEmployers(updatedCheckedEmployers));
    }
  }

  return (  
    <Components.Container isColumn>
      <Components.Panel
        header={translations.RetrieveDocumentsTitle}
        maxWidth='50vw'
        flex='auto'
        isColumn
        footer={
          <Components.Container isRow>
          <Components.Button
            name='retrieve'
            text={translations.RetrieveButton}
            size="small"
            type="HAPPY"
            onClick={handleSend}
            disabled={isSending || !isDataValid}
          />
          <Components.DisplayField
          value={translations.OperationInProgress} 
          dataType="text"
          cssClass={isSending ? '' : 'hidden'}
          />
          </Components.Container>
        }>
        <Components.Container key='retrieveForm' isRow>
          <Components.Container isColumn>
            <Components.InputField
              name='userId'
              label={translations.UserId}
              dataKey='userId'
              dataType='String'
              size='large'
              value={userId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => dispatch(setUserId(e.target.value))}
              mandatory={true}
              validationStatus={(validate && !userId) ? textFieldValidationStates.ERROR : ''}
              description={(validate && !userId) ? translations.NotEmptyValidation : ''}
            />
            <Components.InputField
              name='password'
              label={translations.Password}
              dataKey='password'
              dataType='password'
              size='large'
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => dispatch(setPassword(e.target.value))}
              mandatory={true}
              validationStatus={(validate && !password) ? textFieldValidationStates.ERROR : ''}
              description={(validate && !password) ? translations.NotEmptyValidation : ''}
            />
            <Components.Select
              name='website'
              fieldName='website'
              label={translations.Website}
              options={websiteOptions}
              displayField='name'
              valueField='name'
              size='large'
              value={website}
              onChange={(e: React.ChangeEvent<HTMLOptionElement>) => dispatch(setWebsite(eval(e.target.value)?.name || ''))}
              mandatory={true}
            />
          </Components.Container>
          <Components.Container isColumn>
            <Components.Select
              name='documentType'
              fieldName='documentType'
              label={translations.DocumentType}
              options={documentTypesOptions}
              filtering={true}
              displayField='typeName'
              valueField='typeName'
              size='large'
              value={documentType}
              onChange={(e: React.ChangeEvent<HTMLOptionElement>) => dispatch(setDocumentType(eval(e.target.value)?.typeName || ''))}
              mandatory={true}

              validationType={(validate && !documentType) ? textFieldValidationStates.ERROR : ''}
              validationStatus={(validate && !documentType) ? textFieldValidationStates.ERROR : ''}
              description={(validate && !documentType) ? translations.NotEmptyValidation : ''}
            />
          
            <Components.Table
              height='200px'
              id='employersList'
              onSelectionChanged={handleSelectionChanged}
              onEdit={handleEdit}
              columns={employersTableColumns}
              cssClass={shouldShowEmployersValidation() ? 'validated-table' : ''}
              data={employersWithSequenceNo}
              config={{
                allowAddRow: false,
                allowRemoveRow: false,
                multiselect: true,
                readOnly: false,
                resizableColumns: false,
                pagination: 20,
              }}
            /> 
            <Components.Container isRow 
              cssClass={getTableValidationCssClass()}
              alignItems='center'
              alignContent='flex-start'>
              <Components.Icon icon='exclamation-circle'/>
              <Components.FieldLabel
                fieldType='text'
                text={translations.NotEmptyArrayValdiation}>
              </Components.FieldLabel>
            </Components.Container>
          </Components.Container>
        </Components.Container>
      </Components.Panel>
      <Modal 
        onClose={()=> dispatch(setSuccess(false))} 
        isVisible={isSuccess} 
        type="success"
        notClosable={false}
        body={translations.OperationSuccessfull} 
        title={translations.ModalSuccessTitle}/>
      <Modal 
        onClose={()=> dispatch(setError(false))} 
        isVisible={isError} 
        type="warning" 
        notClosable={false}
        body={errorMessage || translations.OperationError} 
        title={translations.ModalErrorTitle}/>  
      </Components.Container>
  );
};
